import { BreadcrumbLayout } from "@/components/breadcrumb/Breadcrumb";
import { AuthButton } from "@/components/button/AuthButton";
import PageLoader from "@/components/loader/PageLoader";
import CustomAccordionItem from "@/components/profile/AccordionItem";
import { setHeaderText } from "@/redux/features/dashboard/dashboardSlice";
import {
	useApproveClaimMutation,
	useGetClaimQuery,
} from "@/services/requests/dashboard";
import {
	Box,
	Flex,
	FormControl,
	FormLabel,
	Image,
	SimpleGrid,
	Text,
	Textarea,
	useToast,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

interface ClaimInputProps {
	adminMessage: string;
	number: number;
	status: string;
}

const DeclineClaim = () => {
	const { profile } = useParams();
	const navigate = useNavigate();
	const toast = useToast();
	const { isLoading, data } = useGetClaimQuery(profile || "0");
	const [
		declineUser,
		{ isLoading: declineLoading, data: declineData, isSuccess, isError },
	] = useApproveClaimMutation();

	const dispatch = useDispatch();
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<ClaimInputProps>();

	const onSubmit: SubmitHandler<ClaimInputProps> = async (
		data: ClaimInputProps
	) => {
		await declineUser({
			id: profile || "",
			adminMessage: data.adminMessage,
			status: "declined",
		});
		reset();
	};

	useEffect(() => {
		dispatch(setHeaderText("Decline Claim"));
	}, [data]);

	useEffect(() => {
		if (isSuccess) {
			toast({
				title: declineData?.message || "Claimed Declined",
				status: "success",
				duration: 3000,
				isClosable: true,
				position: "top-right",
			});
			navigate("/dashboard/claims");
		}

		if (isError) {
			toast({
				title: declineData?.message || "Not a new Claim",
				status: "error",
				duration: 3000,
				isClosable: true,
				position: "top-right",
			});
		}
	}, [isSuccess, isError]);

	return (
		<Box>
			{isLoading ? (
				<PageLoader py="12rem" />
			) : (
				<>
					<BreadcrumbLayout
						BreadcrumbLinkOne="Home"
						BreadcrumbLinkTwo="Claims"
						BreadcrumbLinkThree={profile}
						BreadcrumbLinkFour="Decline"
						isFourth
					/>
					<Flex
						justify="space-between"
						border="1px solid"
						borderColor="brand.gray500"
						bg="brand.white100"
						borderRadius="5px"
						mt="1.8rem"
					>
						<Box
							as="form"
							w="100%"
							onSubmit={handleSubmit(onSubmit)}
							borderRight="1px solid"
							borderColor="brand.gray500"
							py="2rem"
							px="1.5rem"
						>
							<FormControl mt="1.6rem">
								<FormLabel
									fontWeight="400"
									color="brand.dark100"
									htmlFor="text"
								>
									Additonal notes (if any)
								</FormLabel>

								<Textarea
									border="1px solid #EAEAEA"
									borderRadius="5px"
									{...register("adminMessage", {
										required: "Please enter a message",
										minLength: { value: 6, message: "Text is too short" },
									})}
									_placeholder={{
										fontWeight: 400,
										fontSize: ".9rem",
										color: "brand.dark100",
									}}
									_focus={{
										borderColor: "brand.blue100",
										boxShadow: "none",
									}}
									_hover={{
										borderColor: "none",
									}}
								/>
								{errors.adminMessage && (
									<Text color="brand.red100">
										{errors.adminMessage.message}
									</Text>
								)}
							</FormControl>

							<AuthButton
								text="DECLINE"
								bg="brand.red100"
								bgHover="brand.red100"
								w="45%"
								loading={declineLoading}
							/>
						</Box>

						<Box py="2rem" px="1.5rem">
							<SimpleGrid column={2} spacingX="4rem">
								<CustomAccordionItem title="Claim ID" value={data?.id || ""} />
								<CustomAccordionItem
									title="Date"
									value={moment(data?.claimDate).format("Do MMM YYYY") || ""}
								/>
								<CustomAccordionItem
									title="Reason for claim"
									value={data?.typeOfIncident || ""}
								/>
								<CustomAccordionItem
									title="Details"
									value={data?.description || ""}
								/>
								<CustomAccordionItem
									title="Were you a driver or passenger?"
									value={data?.driverName || ""}
								/>
								<Flex>
									<Text fontSize="sm" color="brand.gray100" w="40%">
										Photos attached
									</Text>
									<SimpleGrid columns={2} spacingX={4} mt={2}>
										<>
											<Image
												w="100%"
												h="auto"
												src={data?.user?.image}
												alt="vehicle"
											/>
											{/* <Image
												w="100%"
												h="auto"
												src={data?.sideView}
												alt="vehicle"
											/> */}
										</>
									</SimpleGrid>
								</Flex>
							</SimpleGrid>
						</Box>
					</Flex>
				</>
			)}
		</Box>
	);
};

export default DeclineClaim;
